// Space Designer (Phase 36 + 0104 draft/approve) — persistence seam.
//
// After migration 0104 the PUT endpoint saves DRAFTS (pending_design_data);
// projection to bookable resources runs only when the operator hits Approve
// (POST /approve). This hook exposes:
//
//   • auto-save (debounced) to the draft
//   • hydrate the store PREFERRING the pending draft if one exists
//   • approve() + discard() imperative helpers for the UI
//   • isDirty flag driven by the GET response
//
// The PUT response no longer carries `blocked` (that only shows on Approve).

import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { usePlanner, setScenePersister } from "./store";
import { computeProjection } from "./projection";
import type { Scene } from "./types";
import {
  useGetSpaceFloorDesign,
  getGetSpaceFloorDesignQueryKey,
  usePutSpaceFloorDesign,
  useApproveSpaceFloorDesign,
  useDiscardSpaceFloorDesign,
} from "@workspace/api-client-react";

function normalizeScene(s: Partial<Scene> | undefined): Scene {
  return {
    items: s?.items ?? [],
    walls: s?.walls ?? [],
    measurements: s?.measurements ?? [],
    areas: s?.areas ?? [],
    blueprint: s?.blueprint ?? null,
    customClusters: s?.customClusters ?? {},
    customRooms: s?.customRooms ?? [],
    teams: s?.teams ?? [],
  };
}

export function useFloorDesignSync(floorId: number | null, pixelsPerFoot: number) {
  const qc = useQueryClient();
  const put = usePutSpaceFloorDesign();
  const approveMut = useApproveSpaceFloorDesign();
  const discardMut = useDiscardSpaceFloorDesign();
  const ppfRef = useRef(pixelsPerFoot);
  ppfRef.current = pixelsPerFoot;

  const designQuery = useGetSpaceFloorDesign(floorId ?? 0, {
    query: {
      enabled: floorId != null,
      queryKey: getGetSpaceFloorDesignQueryKey(floorId ?? 0),
    },
  });

  // Hydrate the store when a floor's design loads. If a pending draft exists,
  // load THAT so the operator picks up exactly where they left off; otherwise
  // load the live design. Also stash the LIVE scene on the store separately
  // so the UI can diff pending vs live for New/Modified/Removed badges.
  const loadedFloorRef = useRef<number | null>(null);
  useEffect(() => {
    if (floorId == null || !designQuery.data) return;
    if (loadedFloorRef.current === floorId) return;
    const d = designQuery.data as unknown as {
      scene: Partial<Scene> | undefined;
      pendingScene: Partial<Scene> | null | undefined;
      isDirty: boolean;
    };
    const liveScene = normalizeScene(d.scene);
    const workingScene = d.pendingScene ? normalizeScene(d.pendingScene) : liveScene;
    usePlanner.setState({ scene: workingScene, selectedIds: [] });
    loadedFloorRef.current = floorId;
  }, [floorId, designQuery.data]);

  // Wire the debounced persister for this floor — writes to pending only.
  useEffect(() => {
    if (floorId == null) {
      setScenePersister(null);
      return;
    }
    let timer: ReturnType<typeof setTimeout> | null = null;
    let pendingScene: Scene | null = null;
    let inflight = false;

    const flush = async () => {
      if (!pendingScene || inflight) return;
      const scene = pendingScene;
      pendingScene = null;
      inflight = true;
      try {
        await put.mutateAsync({
          id: floorId,
          data: {
            scene: scene as unknown as Record<string, unknown>,
            // Projection ignored by the PUT after 0104 but sent so /approve
            // can still consume it when needed. Cheap to compute.
            projection: computeProjection(scene, ppfRef.current),
          },
        });
        // Invalidate the design query so isDirty flips true in the UI.
        qc.invalidateQueries({ queryKey: getGetSpaceFloorDesignQueryKey(floorId) });
      } catch {
        toast.error("Failed to save draft");
      } finally {
        inflight = false;
        if (pendingScene) void flush();
      }
    };

    setScenePersister((scene) => {
      if (loadedFloorRef.current !== floorId) return;
      pendingScene = scene;
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => void flush(), 700);
    });

    return () => {
      if (timer) clearTimeout(timer);
      setScenePersister(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [floorId]);

  // Approve — publish the pending draft, run projection, clear pending.
  async function approve(): Promise<void> {
    if (floorId == null) return;
    // Flush any un-sent local edits first so what we approve matches the
    // canvas state.
    const currentScene = usePlanner.getState().scene;
    try {
      await put.mutateAsync({
        id: floorId,
        data: {
          scene: currentScene as unknown as Record<string, unknown>,
          projection: computeProjection(currentScene, ppfRef.current),
        },
      });
    } catch {
      toast.error("Couldn't save the draft before approving. Try again.");
      return;
    }
    try {
      const res = await approveMut.mutateAsync({
        id: floorId,
        data: {
          projection: computeProjection(currentScene, ppfRef.current) as unknown as Record<string, unknown>,
        },
      });
      const blocked = res?.blocked ?? [];
      if (blocked.length) {
        toast.warning(
          `Published with ${blocked.length} kept — active assignment: ${blocked.map((b) => b.label).join(", ")}`,
        );
      } else if (res && res.ok === false && res.projectionError) {
        toast.error(`Design published, but resource sync failed: ${res.projectionError}`);
      } else {
        toast.success("Floor design published");
      }
      qc.invalidateQueries({ queryKey: getGetSpaceFloorDesignQueryKey(floorId) });
      qc.invalidateQueries({ queryKey: ["/api/space/resources"] });
      qc.invalidateQueries({ queryKey: ["/api/space/workstation-clusters"] });
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Approve failed";
      toast.error(msg);
    }
  }

  // Discard — clear the pending draft. Reload the store with the live scene
  // so the canvas snaps back to what's currently published.
  async function discard(): Promise<void> {
    if (floorId == null) return;
    try {
      await discardMut.mutateAsync({ id: floorId });
      // Refetch the design to get the live scene, then reload the store.
      const refreshed = await qc.fetchQuery({
        queryKey: getGetSpaceFloorDesignQueryKey(floorId),
      });
      const d = refreshed as unknown as { scene: Partial<Scene> | undefined };
      usePlanner.setState({ scene: normalizeScene(d.scene), selectedIds: [] });
      toast.success("Draft discarded");
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Discard failed";
      toast.error(msg);
    }
  }

  const data = designQuery.data as unknown as {
    scene?: Partial<Scene>;
    pendingScene?: Partial<Scene> | null;
    isDirty?: boolean;
  } | undefined;

  return {
    loading: floorId != null && designQuery.isLoading,
    saving: put.isPending,
    approving: approveMut.isPending,
    discarding: discardMut.isPending,
    isDirty: !!data?.isDirty,
    liveScene: data?.scene ? normalizeScene(data.scene) : null,
    approve,
    discard,
  };
}
